import { SortDirection } from '@amzn/stencil-react-components/table'

import { APP_CONFIG } from 'src/config.app'
import {
    getModuleGroupTypeByValue,
    getModuleStatusKeyByValue,
    ModuleGroupSortField,
    ModuleGroupStatus,
    ModuleGroupType,
    QueryParams,
    SearchQuery,
} from 'src/models/dto/module-groups/ModuleGroupTypeDTO'
import { ApiActionNames } from 'src/services/AxiosInterceptor'

export const DEFAULT_PAGE_SIZE = 10

/**
 * Converts URLSearchParams to SearchQuery
 */
export function fromParams(searchParams: URLSearchParams): SearchQuery {
    const offsetSearchParam = searchParams.get(QueryParams.OFFSET) ?? undefined
    const pageSizeSearchParam = searchParams.get(QueryParams.RECORDS_PER_PAGE) ?? undefined
    const sortField = (searchParams.get(QueryParams.SORT_FIELD) ??
        'modified_at') as ModuleGroupSortField
    const sortDirectionParam = searchParams.get(QueryParams.SORT_DIRECTION)
    const sortDirection =
        sortDirectionParam === SortDirection.Ascending.toString()
            ? SortDirection.Ascending
            : SortDirection.Descending

    return {
        search: searchParams.get(QueryParams.SEARCH) ?? '',
        owner: searchParams.get(QueryParams.OWNER) ?? undefined,
        moduleVersionId: searchParams.get(QueryParams.MODULE_VERSION_ID) ?? undefined,
        startDate: searchParams.get(QueryParams.START_DATE) ?? undefined,
        endDate: searchParams.get(QueryParams.END_DATE) ?? undefined,
        moduleGroupType: (searchParams.getAll(QueryParams.GROUP_TYPE) as ModuleGroupType[]) ?? [],
        moduleGroupStatus:
            (searchParams.getAll(QueryParams.GROUP_STATUS) as ModuleGroupStatus[]) ?? [],
        numberOfRecordsPerPage: pageSizeSearchParam ? +pageSizeSearchParam : DEFAULT_PAGE_SIZE,
        offset: offsetSearchParam ? +offsetSearchParam : 0,
        sortField,
        sortDirection,
    }
}

/**
 * Converts SearchQuery to URLSearchParams
 */
export function toSearchParams(searchQuery: SearchQuery, currentPage: number): URLSearchParams {
    const queryParams: string[][] = [
        [QueryParams.SEARCH, searchQuery.search],
        [QueryParams.SELECTED_PAGE, String(currentPage)],
        [QueryParams.OFFSET, String(searchQuery.offset)],
        [QueryParams.RECORDS_PER_PAGE, String(searchQuery.numberOfRecordsPerPage)],
    ]

    if (searchQuery.sortField) {
        queryParams.push([QueryParams.SORT_FIELD, searchQuery.sortField])
    }

    if (searchQuery.sortDirection) {
        queryParams.push([QueryParams.SORT_DIRECTION, searchQuery.sortDirection])
    }

    if (searchQuery.startDate) {
        queryParams.push([QueryParams.START_DATE, searchQuery.startDate])
    }

    if (searchQuery.endDate) {
        queryParams.push([QueryParams.END_DATE, searchQuery.endDate])
    }

    if (searchQuery.owner) {
        queryParams.push([QueryParams.OWNER, searchQuery.owner])
    }

    if (searchQuery.moduleVersionId) {
        queryParams.push([QueryParams.MODULE_VERSION_ID, searchQuery.moduleVersionId])
    }

    if (searchQuery.moduleGroupType && searchQuery.moduleGroupType.length > 0) {
        searchQuery.moduleGroupType.forEach((f) => {
            queryParams.push([QueryParams.GROUP_TYPE, f])
        })
    }

    if (searchQuery.moduleGroupStatus && searchQuery.moduleGroupStatus.length > 0) {
        searchQuery.moduleGroupStatus.forEach((f) => {
            queryParams.push([QueryParams.GROUP_STATUS, f])
        })
    }

    return new URLSearchParams(queryParams)
}

/**
 * Creates the `GetModuleGroup` API request configuration for searching module groups.
 */
export function getSearchRequest(
    searchQuery: SearchQuery,
    backendAPIBaseUrl = APP_CONFIG.backendAPIBaseUrl
) {
    const { sortField, sortDirection, moduleGroupStatus, moduleGroupType, ...nonArrayArguments } =
        searchQuery

    const arrayArguments: Record<string, string[]> = {}
    if (moduleGroupStatus && moduleGroupStatus.length > 0) {
        arrayArguments.moduleGroupStatus = moduleGroupStatus.map((m) =>
            getModuleStatusKeyByValue(m)
        )
    }
    if (moduleGroupType && moduleGroupType.length > 0) {
        arrayArguments.moduleGroupType = moduleGroupType.map((m) => getModuleGroupTypeByValue(m))
    }

    const queryStringParts: string[] = []
    for (const [key, values] of Object.entries(arrayArguments)) {
        for (const value of values) {
            queryStringParts.push(`${key}=${encodeURIComponent(value)}`)
        }
    }
    const arrayQueryString = queryStringParts.join('&')
    return {
        method: 'GET' as const,
        url: `${backendAPIBaseUrl}/module-groups-versions?${arrayQueryString}`,
        params: {
            ...nonArrayArguments,
            [QueryParams.SORT_FIELD]: sortField ? sortField.toUpperCase() : 'MODIFIED_AT',
            [QueryParams.SORT_DIRECTION]: sortDirection
                ? sortDirection.toUpperCase()
                : 'DESCENDING',
        },
        apiActionName: ApiActionNames.GetModuleGroup,
    }
}
